import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Heart, AlertTriangle, CheckCircle, ChevronRight, ShieldAlert, Activity, ChevronDown, ChevronUp, HeartPulse } from 'lucide-react';
import CPRQuickGuide from './CPRQuickGuide';

const PRIORITY_CONFIG = {
  1: { label: 'P1', title: 'Arrêt cardiaque', color: '#DC2626', bg: '#FEE2E2' },
  2: { label: 'P2', title: 'Critique', color: '#F59E0B', bg: '#FEF3C7' },
  4: { label: 'P4', title: 'Stable', color: '#22C55E', bg: '#DCFCE7' },
};

const ProtocolPanel = ({ sessionId }) => {
  const [protocol, setProtocol] = useState(null);
  const [currentPhaseIdx, setCurrentPhaseIdx] = useState(0);
  const [phaseAnswers, setPhaseAnswers] = useState({});
  const [redFlags, setRedFlags] = useState([]);
  const [currentPriority, setCurrentPriority] = useState(4);
  const [completed, setCompleted] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [saved, setSaved] = useState(false);
  const [showPriorityAlert, setShowPriorityAlert] = useState(false);
  const [showCPRGuide, setShowCPRGuide] = useState(false);

  // Auto-detect cardiac arrest red-flag pattern (unconscious + not breathing).
  // The "alert" state pulses the CPR button to call attention without forcing the modal.
  const cprAlertActive = redFlags.some(rf => {
    const t = (rf || '').toLowerCase();
    return (
      t.includes('inconscient') || t.includes('ne respire') ||
      t.includes('arrêt') || t.includes('rcr') ||
      t.includes('respiration agonale') || t.includes('gasps')
    );
  });

  // Refs to access latest state in cleanup (avoid stale closure)
  const stateRef = useRef({ phaseAnswers: {}, redFlags: [], currentPriority: 4, completed: false, saved: false, currentPhaseIdx: 0 });
  stateRef.current = { phaseAnswers, redFlags, currentPriority, completed, saved, currentPhaseIdx };
  const protocolRef = useRef(null);
  protocolRef.current = protocol;

  useEffect(() => {
    axios.get(`${API}/health/protocols/douleur_thoracique`)
      .then(r => setProtocol(r.data))
      .catch(() => {});
  }, []);

  // Save when protocol is completed
  useEffect(() => {
    if (completed && sessionId && !saved) {
      setSaved(true);
      saveProtocol(phaseAnswers, redFlags, currentPriority, true);
    }
  }, [completed, sessionId, saved, phaseAnswers, redFlags, currentPriority]);

  // Auto-save incrementally on any change (debounced), so data is never lost
  useEffect(() => {
    if (!sessionId || !protocol || currentPhaseIdx === 0 || completed) return;
    const t = setTimeout(() => {
      saveProtocol(phaseAnswers, redFlags, currentPriority, false);
    }, 800);
    return () => clearTimeout(t);
  }, [phaseAnswers, redFlags, currentPriority, currentPhaseIdx, sessionId, protocol, completed]);

  // Save on unmount (when student ends session without completing protocol)
  useEffect(() => {
    return () => {
      const s = stateRef.current;
      if (sessionId && !s.saved && s.currentPhaseIdx > 0) {
        saveProtocol(s.phaseAnswers, s.redFlags, s.currentPriority, s.completed);
      }
    };
  }, [sessionId]);

  const saveProtocol = (answers, flags, priority, isCompleted) => {
    // Use ref to ensure latest protocol is available (avoid stale closure on unmount)
    const proto = protocolRef.current;
    // Build structured answers with phase labels for printing
    const structuredAnswers = {};
    if (proto) {
      proto.phases.forEach(phase => {
        if (phase.type === 'info_gathering') {
          // Phase 1: fields are stored directly by field ID (adresse, rappel, etc.)
          const fieldValues = {};
          let hasAny = false;
          phase.fields.forEach(f => {
            if (answers[f.id]) {
              fieldValues[f.id] = { label: f.label, value: answers[f.id] };
              hasAny = true;
            }
          });
          if (hasAny) {
            structuredAnswers[phase.id] = {
              phase_number: phase.number,
              phase_title: phase.title,
              type: phase.type,
              fields: fieldValues,
            };
          }
        } else if (phase.type === 'multi_question') {
          // Phases 6,7,9: answers stored as answers[phase.id] = { q_id: value }
          const ans = answers[phase.id];
          if (ans && typeof ans === 'object') {
            const detailed = {};
            phase.questions.forEach(q => {
              if (ans[q.id] !== undefined) {
                detailed[q.id] = { question: q.question, answer: ans[q.id], red_flag: q.red_flag || false };
              }
            });
            structuredAnswers[phase.id] = {
              phase_number: phase.number,
              phase_title: phase.title,
              type: phase.type,
              details: detailed,
            };
          }
        } else if (phase.type === 'open_question') {
          // Phase 2: stored as answers[phase.id] = [array of checked items]
          const ans = answers[phase.id];
          if (ans) {
            structuredAnswers[phase.id] = {
              phase_number: phase.number,
              phase_title: phase.title,
              type: phase.type,
              checked_items: Array.isArray(ans) ? ans : [],
              all_items: phase.elements_recherches || [],
            };
          }
        } else {
          // decision, comparison: stored as answers[phase.id] = "label"
          const ans = answers[phase.id];
          if (ans !== undefined && ans !== null) {
            structuredAnswers[phase.id] = {
              phase_number: phase.number,
              phase_title: phase.title,
              type: phase.type,
              question: phase.question || '',
              value: ans,
            };
          }
        }
      });
    }
    axios.post(`${API}/sessions/${sessionId}/health-protocol`, {
      protocol_id: 'douleur_thoracique',
      answers: structuredAnswers,
      red_flags: flags,
      final_priority: priority,
      completed: isCompleted,
    }).catch(() => {});
  };

  const advancePhase = () => {
    setCurrentPhaseIdx(prev => {
      if (!protocol) return prev;
      const next = prev + 1;
      if (next >= protocol.phases.length) {
        setCompleted(true);
        return prev;
      }
      return next;
    });
    setShowPriorityAlert(false);
  };

  const handleDecision = (phase, answer) => {
    setPhaseAnswers(prev => ({ ...prev, [phase.id]: answer.label }));
    if (answer.alert) {
      setRedFlags(prev => [...prev, phase.question + ' → ' + answer.label]);
    }
    if (answer.next === 'priority_1') {
      setCurrentPriority(1);
      setRedFlags(prev => [...prev, phase.question + ' → ' + answer.label]);
      // Show priority alert, but DON'T complete — let the student continue
      setShowPriorityAlert(true);
      // Still advance to next phase
      advancePhase();
      return;
    }
    if (answer.priority_change && answer.priority_change < currentPriority) {
      setCurrentPriority(answer.priority_change);
      setShowPriorityAlert(true);
    }
    advancePhase();
  };

  const handleMultiAnswer = (phase, qId, value, isRedFlag) => {
    setPhaseAnswers(prev => ({
      ...prev,
      [phase.id]: { ...(prev[phase.id] || {}), [qId]: value }
    }));
    if (isRedFlag && value === 'oui') {
      setRedFlags(prev => [...prev, phase.questions.find(q => q.id === qId)?.question || qId]);
      setCurrentPriority(prev => Math.min(prev, 2));
    }
  };

  const handleComparison = (phase, answer) => {
    setPhaseAnswers(prev => ({ ...prev, [phase.id]: answer.label }));
    if (answer.id === 'cardiaque') {
      setCurrentPriority(prev => Math.min(prev, 2));
    }
    advancePhase();
  };

  if (!protocol) return null;

  const pConfig = PRIORITY_CONFIG[currentPriority];
  const currentPhase = protocol.phases[currentPhaseIdx];

  const renderPhase = (phase) => {
    if (!phase) return null;

    if (phase.type === 'info_gathering') {
      return (
        <div className="space-y-1.5">
          <p className="text-[11px] text-gray-500 italic">Notez les informations obtenues :</p>
          {phase.fields.map(f => (
            <input key={f.id} type="text" placeholder={f.label} value={phaseAnswers[f.id] || ''}
              onChange={e => setPhaseAnswers(prev => ({ ...prev, [f.id]: e.target.value }))}
              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1"
            />
          ))}
          <button onClick={advancePhase}
            disabled={!phase.fields.filter(f => f.required).every(f => phaseAnswers[f.id]?.trim())}
            className="w-full py-1.5 rounded text-xs font-semibold text-white disabled:opacity-40" style={{ backgroundColor: '#002768' }}>
            Suivant <ChevronRight className="inline w-3 h-3" />
          </button>
        </div>
      );
    }

    if (phase.type === 'open_question') {
      const checked = phaseAnswers[phase.id] || [];
      return (
        <div>
          <div className="p-1.5 rounded bg-blue-50 border border-blue-200 mb-2">
            <p className="text-[11px] font-medium text-blue-800 italic">"{phase.question}"</p>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {phase.elements_recherches.map(el => (
              <label key={el} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded bg-gray-50 cursor-pointer hover:bg-gray-100">
                <input type="checkbox" className="w-2.5 h-2.5"
                  checked={checked.includes(el)}
                  onChange={() => setPhaseAnswers(prev => {
                    const current = prev[phase.id] || [];
                    const updated = current.includes(el) ? current.filter(x => x !== el) : [...current, el];
                    return { ...prev, [phase.id]: updated };
                  })}
                />{el}
              </label>
            ))}
          </div>
          <button onClick={advancePhase} className="w-full py-1.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: '#002768' }}>
            Suivant <ChevronRight className="inline w-3 h-3" />
          </button>
        </div>
      );
    }

    if (phase.type === 'decision') {
      return (
        <div>
          {phase.critical_question && (
            <div className="flex items-center gap-1 mb-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded">
              <AlertTriangle className="w-3 h-3" /> Question critique
            </div>
          )}
          <div className="p-1.5 rounded bg-blue-50 border border-blue-200 mb-2">
            <p className="text-[11px] font-medium text-blue-800 italic">"{phase.question}"</p>
          </div>
          <div className="space-y-1">
            {phase.answers.map(a => (
              <button key={a.id} onClick={() => handleDecision(phase, a)}
                className="w-full text-left p-1.5 rounded border transition-all hover:shadow text-[11px]"
                style={{ borderColor: a.alert ? '#FCA5A5' : '#E5E7EB', backgroundColor: a.alert ? '#FEF2F2' : 'white' }}>
                <span className="font-semibold">{a.label}</span>
                {a.alert && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-full bg-red-100 text-red-700">ALERTE</span>}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (phase.type === 'multi_question') {
      return (
        <div className="space-y-1.5">
          {phase.questions.map(q => (
            <div key={q.id} className="p-1.5 rounded border text-[11px]" style={{ borderColor: q.red_flag && phaseAnswers[phase.id]?.[q.id] === 'oui' ? '#FCA5A5' : '#E5E7EB' }}>
              <p className="font-medium text-gray-700 mb-1">
                {q.red_flag && <ShieldAlert className="inline w-3 h-3 mr-0.5 text-red-500" />}
                {q.question}
              </p>
              {q.type === 'scale' ? (
                <div className="flex items-center gap-2">
                  <input type="range" min="0" max="10" defaultValue="5" className="flex-1 h-1" onChange={e => handleMultiAnswer(phase, q.id, e.target.value, false)} />
                  <span className="text-xs font-bold w-5 text-center">{phaseAnswers[phase.id]?.[q.id] || '5'}</span>
                </div>
              ) : q.type === 'open_text' ? (
                <input
                  type="text"
                  value={phaseAnswers[phase.id]?.[q.id] || ''}
                  onChange={e => handleMultiAnswer(phase, q.id, e.target.value, false)}
                  placeholder="Réponse de l'appelant..."
                  className="w-full px-1.5 py-1 rounded border text-[10px] focus:outline-none"
                  style={{ borderColor: (phaseAnswers[phase.id]?.[q.id] || '').trim() ? '#2E6CF5' : '#E5E7EB' }}
                />
              ) : (
                <div className="flex gap-1">
                  {(q.type === 'yes_no_unknown' ? ['oui', 'non', 'inconnue'] : ['oui', 'non']).map(val => {
                    const labels = { oui: 'Oui', non: 'Non', inconnue: 'Inconnue' };
                    const isSelected = phaseAnswers[phase.id]?.[q.id] === val;
                    const isRedActive = val === 'oui' && q.red_flag && isSelected;
                    const isUnknown = val === 'inconnue' && isSelected;
                    return (
                      <button key={val} onClick={() => handleMultiAnswer(phase, q.id, val, q.red_flag && val === 'oui')}
                        className="flex-1 py-1 rounded text-[10px] font-semibold border transition-all"
                        style={{
                          borderColor: isRedActive ? '#DC2626' : isUnknown ? '#F59E0B' : isSelected ? '#2E6CF5' : '#E5E7EB',
                          backgroundColor: isRedActive ? '#FEE2E2' : isUnknown ? '#FEF3C7' : isSelected ? '#EFF6FF' : 'white',
                          color: isRedActive ? '#DC2626' : isUnknown ? '#92400E' : isSelected ? '#2E6CF5' : '#6B7280',
                        }}>
                        {labels[val]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <button onClick={advancePhase} className="w-full py-1.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: '#002768' }}>
            Suivant <ChevronRight className="inline w-3 h-3" />
          </button>
        </div>
      );
    }

    if (phase.type === 'comparison') {
      return (
        <div>
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <div className="p-1.5 rounded bg-amber-50 border border-amber-200">
              <h4 className="text-[9px] font-bold text-amber-800 mb-0.5">Anxiété</h4>
              {phase.anxiety_symptoms.map(s => <p key={s} className="text-[9px] text-amber-700">• {s}</p>)}
            </div>
            <div className="p-1.5 rounded bg-red-50 border border-red-200">
              <h4 className="text-[9px] font-bold text-red-800 mb-0.5">Cardiaque</h4>
              {phase.red_flags_cardiac.map(s => <p key={s} className="text-[9px] text-red-700">• {s}</p>)}
            </div>
          </div>
          <div className="space-y-1">
            {phase.answers.map(a => (
              <button key={a.id} onClick={() => handleComparison(phase, a)}
                className="w-full text-left p-1.5 rounded border text-[11px] font-semibold hover:shadow transition-all">
                {a.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="glass border-0 rounded-xl mb-4 overflow-hidden" data-testid="protocol-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4 text-red-500" />
          <span className="text-sm font-bold" style={{ color: '#002768' }}>Protocole Santé</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: pConfig.bg, color: pConfig.color }}>
            {pConfig.label}
          </div>
          {redFlags.length > 0 && (
            <div className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
              {redFlags.length}
            </div>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3">
          {/* Phase dots - clickable for visited phases */}
          <div className="flex items-center gap-1 mb-2 flex-wrap">
            {protocol.phases.map((phase, idx) => {
              const isDone = idx < currentPhaseIdx || completed;
              const isActive = idx === currentPhaseIdx && !completed;
              const canNavigate = isDone && !completed;
              return (
                <button key={phase.id}
                  onClick={canNavigate ? () => { setCurrentPhaseIdx(idx); setShowPriorityAlert(false); } : undefined}
                  disabled={!canNavigate && !isActive}
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition-all ${canNavigate ? 'cursor-pointer hover:ring-2 hover:ring-offset-1' : ''}`}
                  style={{
                    backgroundColor: isDone ? '#22C55E' : isActive ? '#2E6CF5' : '#E5E7EB',
                    color: isDone || isActive ? 'white' : '#9CA3AF',
                    '--tw-ring-color': '#22C55E',
                  }}
                  title={canNavigate ? `Revenir à: Phase ${phase.number} — ${phase.title}` : phase.title}>
                  {isDone ? '✓' : phase.number}
                </button>
              );
            })}
          </div>

          {completed ? (
            <div className="space-y-2">
              <div className="p-2 rounded-lg border-2" style={{ borderColor: pConfig.color, backgroundColor: pConfig.bg }}>
                <h3 className="text-xs font-bold" style={{ color: pConfig.color }}>{pConfig.label} — {pConfig.title}</h3>
                <p className="text-[10px] mt-1" style={{ color: pConfig.color }}>Protocole complété</p>
              </div>
              {redFlags.length > 0 && (
                <div className="p-2 rounded bg-red-50 border border-red-200">
                  <h4 className="text-[10px] font-bold text-red-700 mb-1">Drapeaux rouges</h4>
                  {redFlags.map((f, i) => <p key={`${i}-${f}`} className="text-[10px] text-red-600">⚠ {f}</p>)}
                </div>
              )}
              {protocol.priorities[String(currentPriority)] && (
                <div className="p-2 rounded bg-white border">
                  <h4 className="text-[10px] font-bold text-gray-700 mb-1">Ressources</h4>
                  {protocol.priorities[String(currentPriority)].resources.map(r => <p key={r} className="text-[10px] text-gray-600">• {r}</p>)}
                  <h4 className="text-[10px] font-bold text-gray-700 mt-1 mb-0.5">Consignes pré-arrivée</h4>
                  {protocol.priorities[String(currentPriority)].pre_arrival.map(c => <p key={c} className="text-[10px] text-gray-600">• {c}</p>)}
                </div>
              )}
            </div>
          ) : currentPhase ? (
            <div>
              {/* Priority alert */}
              {showPriorityAlert && currentPriority <= 2 && protocol.priorities[String(currentPriority)] && (
                <div className="p-2 rounded-lg border-2 mb-2" style={{ borderColor: pConfig.color, backgroundColor: pConfig.bg }}>
                  <h4 className="text-[10px] font-bold" style={{ color: pConfig.color }}>{pConfig.label} — {pConfig.title}</h4>
                  <div className="mt-1">
                    {protocol.priorities[String(currentPriority)].resources.map(r => <p key={r} className="text-[10px]" style={{ color: pConfig.color }}>• {r}</p>)}
                  </div>
                  <button onClick={() => setShowPriorityAlert(false)} className="w-full mt-1.5 py-1 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: '#002768' }}>
                    Continuer le protocole <ChevronRight className="inline w-3 h-3" />
                  </button>
                </div>
              )}
              {!showPriorityAlert && (
                <>
                  <div className="flex items-center gap-1 mb-1.5">
                    <Activity className="w-3 h-3" style={{ color: '#002768' }} />
                    <span className="text-[11px] font-bold" style={{ color: '#002768' }}>Phase {currentPhase.number} — {currentPhase.title}</span>
                  </div>
                  {renderPhase(currentPhase)}
                </>
              )}

              {/* CPR Quick Guide button — pulses if cardiac arrest pattern detected */}
              <button
                type="button"
                onClick={() => setShowCPRGuide(true)}
                className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-white text-xs font-bold shadow transition-all ${cprAlertActive ? 'animate-pulse ring-4 ring-red-300' : ''}`}
                style={{ backgroundColor: '#DC2626' }}
                data-testid="open-cpr-guide-button"
                title="Guide rapide pour assister un RCR par téléphone"
              >
                <HeartPulse className="w-4 h-4" />
                {cprAlertActive ? '🫀 GUIDE RCR — URGENCE' : '🫀 Guide RCR (assistance téléphonique)'}
              </button>
            </div>
          ) : null}
        </div>
      )}

      <CPRQuickGuide open={showCPRGuide} onClose={() => setShowCPRGuide(false)} />
    </div>
  );
};

export default ProtocolPanel;
